/** @jsxRuntime classic */
/** @jsx jsx */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { css, jsx } from '@emotion/react';

import { Typography as Text } from '@mui/material';
import TextField from '@mui/material/TextField';

import { useTranscriptUploadActions } from '../../actions/FileActions';

import LowPriorityMessage from './LowPriorityMessage';
import DialogHeader from './DialogHeader';
import InsertLinkIcon from '@mui/icons-material/InsertLink';
import Button from '@mui/material/Button';

import UploadSettings from './UploadSettings';

import UploadProgress from './UploadProgress';

const youtubeRegex =
  /^(?:https?:\/\/)?(?:www\.|m\.)?(?:youtube\.com\/watch\?v=|youtu.be\/)([\w-_]{11})/;

export default function UploadUrl({ handleClose, exceedsUploadLimit }) {
  const { t } = useTranslation();
  const { uploadTranscriptAsync } = useTranscriptUploadActions();

  const [settings, setSettings] = useState({
    numSpeakers: 0,
    language: 'auto',
    outputFormat: 'subtitle',
    transcribeMode: 0,
    url: '',
  });

  const [step, setStep] = useState(0);

  const urlError =
    !youtubeRegex.test(settings.url) &&
    settings.url.length > 0 &&
    !exceedsUploadLimit;

  async function onUpload() {
    await uploadTranscriptAsync({
      ...settings,
    });
    setStep(1);
  }

  if (step === 1) {
    return (
      <div>
        <UploadProgress uploadDone onClose={handleClose} />
      </div>
    );
  }

  return (
    <div
      css={css`
        display: flex;
        flex-direction: column;
        gap: 1rem;
      `}
    >
      <DialogHeader
        title={t('Importera från URL')}
        icon={InsertLinkIcon}
        onClose={handleClose}
      />
      <div>
        <LowPriorityMessage />
        <Text variant="subtitle2" color="text.secondary" fontWeight="600">
          {t(
            'Importera filer från YouTube. Youtube-länken måste vara till en publik video och inte ett livesändning.'
          )}
        </Text>
        <TextField
          variant="outlined"
          fullWidth
          size="small"
          css={css`
            margin-top: 1rem;
          `}
          value={settings.url}
          onChange={(e) => setSettings({ ...settings, url: e.target.value })}
          error={urlError}
          helperText={
            urlError ? t('Länken måste vara en giltig länk till YouTube') : ''
          }
          placeholder="https://"
          disabled={exceedsUploadLimit}
        />
      </div>
      <div>
        <Text variant="subtitle2" fontWeight="700">
          {t('Filnamn')}
        </Text>
        <TextField
          value={settings.name}
          onChange={(e) => setSettings({ ...settings, name: e.target.value })}
          variant="outlined"
          fullWidth
          size="small"
          disabled={exceedsUploadLimit}
        />
      </div>
      <Button
        onClick={onUpload}
        disabled={urlError || exceedsUploadLimit || !settings.name}
        variant="contained"
        color="primary"
        fullWidth
      >
        {t('Importera URL')}
      </Button>
      <UploadSettings
        settings={settings}
        setSettings={setSettings}
        disabled={exceedsUploadLimit}
      />
    </div>
  );
}
